// contexts/ScenarioContext.js - Enhanced Production Version (Backward Compatible)
import { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useSnackbar } from 'notistack';

// Preserve existing export structure
export const ScenarioContext = createContext();

export function ScenarioProvider({ children }) {
  // Use the useAuth hook to get authentication state including refreshUser
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Get token from localStorage
  const token = localStorage.getItem('authToken');

  // Enhanced error handler - moved inside useCallback or made stable with useMemo
  const handleError = useCallback((error, defaultMessage) => {
    console.error('Scenario Context Error:', error);
    
    if (error.response?.status === 401) {
      enqueueSnackbar('Session expired. Please login again.', { variant: 'error' });
      logout();
      return 'Authentication expired';
    } else if (error.response?.status === 403) {
      return 'Insufficient permissions';
    } else if (error.response?.data?.error) {
      return error.response.data.error;
    } else if (error.code === 'NETWORK_ERROR') {
      return 'Network error. Please check your connection.';
    } else if (error.code === 'ECONNABORTED') {
      return 'Request timeout. Please try again.';
    }
    
    return defaultMessage || 'An unexpected error occurred';
  }, [enqueueSnackbar, logout]);

  // Fetch all scenarios for the current user - PRESERVED EXISTING FUNCTIONALITY
  const fetchScenarios = useCallback(async () => {
    if (!isAuthenticated || !user?._id || !token) {
      console.log('Skipping fetch - missing auth requirements');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Fetching scenarios for user:', user._id);
      
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/scenarios/user/${user._id}`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
      
      const scenariosData = data.data || data || [];
      console.log(`Found ${scenariosData.length} scenarios`);
      setScenarios(scenariosData);
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Fetch scenarios failed:', error);
      
      if (error.response?.status === 401) {
        enqueueSnackbar('Session expired. Please login again.', { variant: 'error' });
        logout();
      } else {
        const errorMsg = error.response?.data?.error || 'Failed to load scenarios';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  }, [user?._id, token, isAuthenticated, enqueueSnackbar, logout]);

  // Save a new scenario - PRESERVED EXISTING FUNCTIONALITY
  const saveScenario = useCallback(async (scenarioData) => {
    if (!isAuthenticated || !user?._id || !token) {
      throw new Error('User not authenticated');
    }

    if (user?.credits <= 0) {
      throw new Error('Insufficient credits');
    }
    
    try {
      // Prepare data with consistent user identification
      const dataToSave = {
        ...scenarioData,
        userId: user._id,
        userEmail: user.email
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/scenarios`,
        dataToSave,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
      
      const savedScenario = data.data || data;
    
      
      // Update local state immediately
      setScenarios(prev => {
        const filtered = prev.filter(s => s._id !== savedScenario._id);
        return [...filtered, savedScenario];
      });
      
      // Refresh user data to get updated credits - NOW WORKING
      if (refreshUser) {
        await refreshUser();
      }
      
      return savedScenario;
      
    } catch (error) {
      console.error('Save scenario failed:', error);
      
      let errorMessage = error.message || 'Failed to save scenario';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication expired';
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw new Error(errorMessage);
    }
  }, [user, token, isAuthenticated, enqueueSnackbar, refreshUser]);

  // Delete a scenario - PRESERVED EXISTING FUNCTIONALITY
  const deleteScenario = useCallback(async (scenarioId) => {
    if (!isAuthenticated || !token) {
      throw new Error('Not authenticated');
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/scenarios/${scenarioId}`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
      
      // Update local state immediately
      setScenarios(prev => prev.filter(s => s._id !== scenarioId));
      
    } catch (error) {
      console.error('Delete scenario failed:', error);
      
      let errorMessage = error.message || 'Failed to delete scenario';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication expired';
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw new Error(errorMessage);
    }
  }, [token, isAuthenticated, enqueueSnackbar]);

  // NEW: Duplicate scenario functionality
  const duplicateScenario = useCallback(async (scenarioId) => {
    if (!isAuthenticated || !user?._id || !token) {
      throw new Error('Not authenticated');
    }

    if (user?.credits <= 0) {
      throw new Error('Insufficient credits to duplicate scenario');
    }

    try {
      console.log('Duplicating scenario:', scenarioId);
      
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/scenarios/${scenarioId}/duplicate`,
        {},
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );
      
      const duplicatedScenario = data.data || data;
      console.log('Scenario duplicated successfully:', duplicatedScenario._id);
      
      // Update local state immediately
      setScenarios(prev => [duplicatedScenario, ...prev]);
      
      // Refresh user data to get updated credits
      if (refreshUser) {
        await refreshUser();
      }
      
      enqueueSnackbar(`Scenario "${duplicatedScenario.name}" duplicated successfully!`, { 
        variant: 'success' 
      });
      
      return duplicatedScenario;
      
    } catch (error) {
      const errorMessage = handleError(error, 'Failed to duplicate scenario');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw new Error(errorMessage);
    }
  }, [user, token, isAuthenticated, enqueueSnackbar, refreshUser, handleError]);

  // NEW: Update scenario functionality
  const updateScenario = useCallback(async (scenarioId, updateData) => {
    if (!isAuthenticated || !token) {
      throw new Error('Not authenticated');
    }

    try {
      console.log('Updating scenario:', scenarioId);
      
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/scenarios/${scenarioId}`,
        {
          ...updateData,
          updatedAt: new Date().toISOString()
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );
      
      const updatedScenario = data.data || data;
      
      // Update local state immediately
      setScenarios(prev => prev.map(s => 
        s._id === scenarioId ? updatedScenario : s
      ));
      
      enqueueSnackbar(`Scenario "${updatedScenario.name}" updated successfully!`, { 
        variant: 'success' 
      });
      
      return updatedScenario;
      
    } catch (error) {
      const errorMessage = handleError(error, 'Failed to update scenario');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw new Error(errorMessage);
    }
  }, [token, isAuthenticated, enqueueSnackbar, handleError]);

  // NEW: Create scenario (alias for saveScenario for compatibility)
  const createScenario = useCallback(async (scenarioData) => {
    return await saveScenario(scenarioData);
  }, [saveScenario]);

  // NEW: Get scenario by ID
  const getScenarioById = useCallback((scenarioId) => {
    return scenarios.find(s => s._id === scenarioId);
  }, [scenarios]);

  // Refresh scenarios manually - PRESERVED EXISTING FUNCTIONALITY
  const refreshScenarios = useCallback(() => {
    if (isAuthenticated && user?._id && token) {
      fetchScenarios();
    }
  }, [fetchScenarios, isAuthenticated, user?._id, token]);

  // NEW: Clear all scenarios (useful for logout)
  const clearScenarios = useCallback(() => {
    setScenarios([]);
    setLastUpdated(null);
  }, []);

  // NEW: Get scenarios statistics
  const getScenarioStats = useCallback(() => {
    const totalScenarios = scenarios.length;
    const totalImpact = scenarios.reduce((sum, scenario) => sum + (scenario.totalImpact || 0), 0);
    const averageROI = scenarios.length > 0 
      ? scenarios.reduce((sum, scenario) => {
          const investment = (scenario.turnover || 100000000) * 0.05;
          return sum + (investment > 0 ? (scenario.totalImpact || 0) / investment : 0);
        }, 0) / scenarios.length
      : 0;

    return {
      totalScenarios,
      totalImpact,
      averageROI,
      lastUpdated
    };
  }, [scenarios, lastUpdated]);

  // Auto-fetch scenarios when auth state changes - PRESERVED EXISTING FUNCTIONALITY
  useEffect(() => {
    if (isAuthenticated && user?._id && token) {
      fetchScenarios();
    } else {
      setScenarios([]);
    }
  }, [fetchScenarios, isAuthenticated, user?._id, token]);

  // Value object with ALL functions (existing + new)
  const value = {
    // PRESERVED EXISTING STATE AND FUNCTIONS
    scenarios,
    setScenarios, // Existing
    loading, // Existing
    fetchScenarios: refreshScenarios, // Existing alias
    saveScenario, // Existing
    deleteScenario, // Existing
    refreshScenarios, // Existing
    
    // NEW ADDITIONS (backward compatible)
    duplicateScenario, // New
    createScenario, // New
    updateScenario, // New
    getScenarioById, // New
    clearScenarios, // New
    getScenarioStats, // New
    lastUpdated // New
  };

  return (
    <ScenarioContext.Provider value={value}>
      {children}
    </ScenarioContext.Provider>
  );
}

// PRESERVED EXISTING EXPORT
export const useScenario = () => {
  const context = useContext(ScenarioContext);
  if (!context) {
    throw new Error('useScenario must be used within a ScenarioProvider');
  }
  return context;
};

// NEW: Additional export for compatibility with different naming conventions
export const useScenarioContext = useScenario;